import { useEffect, useRef, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Terminal, HelpCircle } from "lucide-react";
import { api, errMsg } from "@/lib/api";

const CHEATS = [
  ["512 enroute", "set 512 En Route on its current incident"],
  ["512 513 arrived", "multiple units, alias → On Scene"],
  ["301 2026-000123", "assign 301 to the incident"],
  ["2026-000123 301 enroute", "order doesn't matter"],
  ["512 clear", "clear → Available (removes from call)"],
  ["301 traffic", "officer-initiated Traffic Stop for 301"],
  ["512 remove", "remove 512 from its incident"],
  ["101 oos", "Out of Service"],
];

export function CommandBar({ onIncidentCreated }) {
  const [text, setText] = useState("");
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);
  const [showHelp, setShowHelp] = useState(false);
  const inputRef = useRef(null);
  const qc = useQueryClient();

  useEffect(() => {
    const focus = () => inputRef.current?.focus();
    window.addEventListener("cad:focus-command", focus);
    return () => window.removeEventListener("cad:focus-command", focus);
  }, []);

  useEffect(() => {
    if (!text.trim()) { setPreview(null); return undefined; }
    const t = setTimeout(async () => {
      try { const { data } = await api.post("/command/parse", { text }); setPreview(data); } catch { /* ignore */ }
    }, 220);
    return () => clearTimeout(t);
  }, [text]);

  const run = async (actions) => {
    setBusy(true);
    try {
      const { data } = await api.post("/command/execute", actions ? { actions } : { text });
      if (!data.ok) { setPreview(data); return; }
      toast.success(data.echo || "Command executed");
      const created = (data.results || []).find((r) => r && r.officer_initiated);
      if (created && onIncidentCreated) onIncidentCreated(created.incident_id);
      setText(""); setPreview(null);
      qc.invalidateQueries({ queryKey: ["board"] });
      qc.invalidateQueries({ queryKey: ["incident"] });
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };

  const onKeyDown = (e) => {
    if (e.key === "Enter" && text.trim() && !busy) { e.preventDefault(); run(); }
    if (e.key === "Escape") { setText(""); setPreview(null); inputRef.current?.blur(); }
  };

  return (
    <div className="shrink-0 px-2 pb-2" data-testid="command-bar">
      <div className="pane !overflow-visible">
        {showHelp && (
          <div className="px-3 py-2 border-b grid grid-cols-2 lg:grid-cols-4 gap-x-4 gap-y-1" data-testid="command-cheatsheet">
            {CHEATS.map(([c, d]) => <div key={c} className="text-[11px]"><span className="mono text-primary">{c}</span> <span className="text-muted-foreground">— {d}</span></div>)}
          </div>
        )}
        <div className="flex items-center gap-2 px-2 py-1.5">
          <Terminal className="h-4 w-4 text-teal-500 shrink-0" />
          <input
            ref={inputRef}
            data-testid="command-input"
            value={text}
            disabled={busy}
            onChange={(e) => setText(e.target.value)}
            onKeyDown={onKeyDown}
            placeholder="Command bar — e.g. 512 513 enroute · 512 arrived · 301 traffic   (press / to focus)"
            className="flex-1 mono text-sm bg-transparent outline-none placeholder:text-muted-foreground/70"
            autoComplete="off"
            spellCheck={false}
          />
          <button data-testid="command-help-toggle" onClick={() => setShowHelp((s) => !s)} className="text-muted-foreground hover:text-foreground" aria-label="Command cheat sheet"><HelpCircle className="h-4 w-4" /></button>
        </div>
        {preview && (
          <div className="px-3 pb-1.5 -mt-0.5 flex flex-wrap items-center gap-2" data-testid="command-echo">
            {preview.error ? (
              <span className="mono text-xs text-red-600 dark:text-red-300">✕ {preview.error}</span>
            ) : (
              <span className="mono text-xs text-teal-700 dark:text-teal-300">→ {preview.echo}</span>
            )}
            {preview.needs_confirmation && (
              <span className="flex flex-wrap gap-1.5" data-testid="command-options">
                <span className="text-xs text-amber-600 dark:text-amber-300">Confirm:</span>
                {preview.options.map((o, i) => (
                  <button key={i} data-testid={`command-option-${i}`} onClick={() => run(o.actions)} className="h-6 px-2 rounded-md border text-[11px] hover:bg-accent">{o.label}</button>
                ))}
                <button data-testid="command-cancel" onClick={() => { setText(""); setPreview(null); }} className="h-6 px-2 rounded-md text-[11px] text-muted-foreground hover:bg-accent">Cancel</button>
              </span>
            )}
            {!preview.error && !preview.needs_confirmation && <span className="text-[11px] text-muted-foreground">Enter to execute</span>}
          </div>
        )}
      </div>
    </div>
  );
}
